import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from "react";
import { CommentType } from "../../../../dtos/CommentType";
import { FileDto } from "../../../../dtos/FileDto";
import { PhaseDto } from "../../../../dtos/PhaseDto";
import { StepDto } from "../../../../dtos/StepDto";

export type TasklistMode = "default" | "templates";

// split contexts to prevent unnecessary re-renders

export interface ITaskListSelectionState {
    selectedStep: StepDto | null;
    selectedPhase: PhaseDto | null;
    tasklistMode: TasklistMode;
    // Which resubmission cycle's steps are being viewed. null = the latest (active) cycle.
    selectedSubmissionId: string | null;
}

export interface ITaskListActions {
    setSelectedStep: (step: StepDto | null) => void;
    setSelectedPhase: (phase: PhaseDto | null) => void;
    setTasklistMode: (mode: TasklistMode) => void;

    // Resubmission cycle navigation. The setter updates a ref synchronously so a
    // following refreshSteps() reload reads the new cycle without waiting for re-render.
    setSelectedSubmissionId: (submissionId: string | null) => void;
    getSelectedSubmissionId: () => string | null;

    registerForClear: (func: () => void) => void;
    clearSelections: () => void;
    // Runs the registered clear handlers (grid deselection, summary state) without dropping the
    // context selection. Selecting a subtask uses this so the other tasks let go of their rows
    // while the detail panel keeps showing something (ticket 1034).
    clearGridSelections: () => void;

    registerForRefreshSteps: (func: (selectedStep?: StepDto) => Promise<void>) => () => void;
    refreshSteps: (selectedStep?: StepDto) => Promise<void>;

    registerForRefreshFiles: (func: () => void) => void;
    refreshFiles: (opts?: { skipCacheClear?: boolean }) => void;

    registerForRefreshComments: (func: () => void) => void;
    refreshComments: () => void;

    registerForRefreshSelectedStepFiles: (func: () => void) => void;
    refreshSelectedStepFiles: () => void;

    setSelectedStepFiles: (type: CommentType, files: FileDto[], stepId?: string) => void;
    removeSelectedStepFile: (type: CommentType, fileId: string) => void;
    getSelectedStepFiles: (type: CommentType) => FileDto[] | null;
    hasStepFilesCache: (type: CommentType, stepId?: string) => boolean;
}

// legacy interface for backward compatibility

export interface ITaskListContext extends ITaskListSelectionState, ITaskListActions {
    getSelectedStep: () => StepDto | null;
    getSelectedPhase: () => PhaseDto | null;
}

interface ITaskListContextProvider {
    children: any;
    setSelectedStep: (step: StepDto | null) => void;
    setSelectedPhase: (phase: PhaseDto | null) => void;
    tasklistMode?: TasklistMode;
}

const TaskListSelectionStateContext = createContext<ITaskListSelectionState | undefined>(undefined);
const TaskListActionsContext = createContext<ITaskListActions | undefined>(undefined);
export const TaskListContext = createContext<ITaskListContext | undefined>(undefined);

export const useTaskListSelectionState = () => {
    const context = useContext(TaskListSelectionStateContext);
    if (!context) {
        throw new Error("useTaskListSelectionState must be used within a TaskListProvider");
    }
    return context;
};

export const useTaskListActions = () => {
    const context = useContext(TaskListActionsContext);
    if (!context) {
        throw new Error("useTaskListActions must be used within a TaskListProvider");
    }
    return context;
};

// legacy hook for backward compatibility

export const useTaskListContext = () => {
    const state = useTaskListSelectionState();
    const actions = useTaskListActions();
    return useMemo<ITaskListContext>(() => ({
        ...state,
        ...actions,
        getSelectedStep: () => state.selectedStep,
        getSelectedPhase: () => state.selectedPhase,
    }), [state, actions]);
};

const EMPTY_FILES: FileDto[] = [];

function filesEqual(a?: FileDto[] | null, b?: FileDto[] | null) {
    if (a === b) return true;
    const aa = a ?? EMPTY_FILES;
    const bb = b ?? EMPTY_FILES;
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i++) if (aa[i].id !== bb[i].id) return false;
    return true;
}

type State = {
    selectedStep: StepDto | null;
    selectedPhase: PhaseDto | null;
    tasklistMode: TasklistMode;
    selectedSubmissionId: string | null;
};

type Action =
    | { type: "SELECT_STEP"; payload: StepDto | null }
    | { type: "SELECT_PHASE"; payload: PhaseDto | null }
    | { type: "CLEAR" }
    | { type: 'SET_TASKLIST_MODE'; payload: TasklistMode }
    | { type: 'SET_SUBMISSION_ID'; payload: string | null };

const initialState: State = {
    selectedStep: null,
    selectedPhase: null,
    tasklistMode: "default",
    selectedSubmissionId: null
};

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "SELECT_STEP":
            if (state.selectedStep === action.payload) return state;
            return { ...state, selectedStep: action.payload };
        case "SELECT_PHASE":
            if (state.selectedPhase === action.payload) return state;
            // Cycle selection is NOT reset here. It belongs to a phase and is cleared
            // explicitly (SET_SUBMISSION_ID null) only on a real phase change, so it
            // survives clearSelections() + same-phase re-selects (step <-> summary nav).
            return { ...state, selectedPhase: action.payload };
        case "CLEAR":
            if (state.selectedStep === null && state.selectedPhase === null) return state;
            return { ...state, selectedStep: null, selectedPhase: null };
        case 'SET_TASKLIST_MODE':
            if (state.tasklistMode === action.payload) return state;
            return { ...state, tasklistMode: action.payload };
        case 'SET_SUBMISSION_ID':
            if (state.selectedSubmissionId === action.payload) return state;
            return { ...state, selectedSubmissionId: action.payload };
        default:
            return state;
    }
}

export function TaskListProvider({
    children,
    setSelectedStep: externalSetSelectedStep,
    setSelectedPhase: externalSetSelectedPhase,
    tasklistMode
}: ITaskListContextProvider) {

    const [state, dispatch] = useReducer(
        reducer,
        null,
        () => ({
            selectedStep: null,
            selectedPhase: null,
            tasklistMode: tasklistMode ?? 'default',
            selectedSubmissionId: null,
        })
    );

    useEffect(() => {
        if (tasklistMode && tasklistMode !== state.tasklistMode) {
            dispatch({ type: "SET_TASKLIST_MODE", payload: tasklistMode });
        }
    }, [tasklistMode, state.tasklistMode]);

    const selectedStepFilesByStep = useRef<Record<string, Partial<Record<CommentType, FileDto[] | null>>>>({});
    const selectedStepRef = useRef(state.selectedStep);
    // Synchronous mirror of selectedSubmissionId, read by the step loader at reload time.
    const selectedSubmissionIdRef = useRef<string | null>(null);
    // The phase currently in view, and the phase the cycle selection belongs to. The cycle
    // is dropped only when a new phase differs from its owner — so it survives clears and
    // same-phase re-selects (step <-> summary navigation) but resets on a real phase switch.
    const activePhaseIdRef = useRef<string | null>(null);
    const cyclePhaseIdRef = useRef<string | null>(null);
    const getActiveStepId = () => selectedStepRef.current?.id ?? "";
    const clearHandlers = useRef<Set<() => void>>(new Set());

    useEffect(() => {
        selectedStepRef.current = state.selectedStep;
    }, [state.selectedStep]);
    const refreshStepsHandlers = useRef<Set<(selectedStep?: StepDto) => Promise<void>>>(new Set());
    const refreshFilesHandlers = useRef<Set<() => void>>(new Set());
    const refreshCommentsHandler = useRef<(() => void) | undefined>();
    const refreshSelectedStepFilesHandlers = useRef<Set<() => void>>(new Set());
    const registerForClear = useCallback((fn: () => void) => {
        clearHandlers.current.add(fn);
    }, []);

    const setSelectedSubmissionId = useCallback((submissionId: string | null) => {
        selectedSubmissionIdRef.current = submissionId;
        // Remember which phase this cycle belongs to (the one currently in view).
        if (submissionId !== null) cyclePhaseIdRef.current = activePhaseIdRef.current;
        dispatch({ type: "SET_SUBMISSION_ID", payload: submissionId });
    }, []);

    const getSelectedSubmissionId = useCallback(() => selectedSubmissionIdRef.current, []);

    const clearGridSelections = useCallback(() => {
        if (!clearHandlers.current.size) return;
        clearHandlers.current.forEach((fn) => {
            try {
                fn();
            } catch { }
        });
        clearHandlers.current.clear();
    }, []);

    const clearSelections = useCallback(() => {
        // Note: cycle selection is intentionally preserved across clears; it resets on a
        // real phase change (see setSelectedPhase).
        dispatch({ type: "CLEAR" });
        clearGridSelections();
    }, [clearGridSelections]);

    const setTasklistMode = useCallback((mode: TasklistMode) => {
        dispatch({ type: 'SET_TASKLIST_MODE', payload: mode });
    }, []);

    const registerForRefreshSteps = useCallback(
        (fn: (selectedStep?: StepDto) => Promise<void>) => {
            refreshStepsHandlers.current.add(fn);
            return () => { refreshStepsHandlers.current.delete(fn); };
        },
        []
    );

    const refreshSteps = useCallback(async (selectedStep?: StepDto) => {
        const handlers = Array.from(refreshStepsHandlers.current);
        await Promise.all(
            handlers.map(async (fn) => {
                try {
                    await fn(selectedStep);
                } catch { }
            })
        );
    }, []);

    const registerForRefreshFiles = useCallback((fn: () => void) => {
        refreshFilesHandlers.current.add(fn);
    }, []);

    const refreshFiles = useCallback((opts?: { skipCacheClear?: boolean }) => {
        const stepId = getActiveStepId();
        // Skip cache clear when explicitly requested (e.g., templates mode after upload)
        if (stepId && !opts?.skipCacheClear) {
            selectedStepFilesByStep.current[stepId] = {};
        }

        if (refreshFilesHandlers.current.size) {
            refreshFilesHandlers.current.forEach((fn) => {
                try {
                    fn();
                } catch { }
            });
        }
        // The selected step is read through a ref (getActiveStepId), so this stays out of the
        // deps: an action that changed identity on every selection took the whole actions object
        // with it, and any effect depending on that object re-ran mid-selection (ticket 1066).
    }, []);

    const registerForRefreshComments = useCallback((fn: () => void) => {
        refreshCommentsHandler.current = fn;
    }, []);

    const refreshComments = useCallback(() => {
        const fn = refreshCommentsHandler.current;
        if (typeof fn === "function") fn();
    }, []);

    const registerForRefreshSelectedStepFiles = useCallback((fn: () => void) => {
        refreshSelectedStepFilesHandlers.current.add(fn);
    }, []);

    const refreshSelectedStepFiles = useCallback(() => {
        if (refreshSelectedStepFilesHandlers.current.size) {
            refreshSelectedStepFilesHandlers.current.forEach((fn) => {
                try {
                    fn();
                } catch { }
            });
        }
    }, []);

    const setSelectedStepCb = useCallback(
        (step: StepDto | null) => {
            // Create a shallow copy to prevent Form mutations from affecting the original
            // object in the data source (which would cause list updates without saving)
            const stepCopy = step ? { ...step } : null;
            dispatch({ type: "SELECT_STEP", payload: stepCopy });
            externalSetSelectedStep(stepCopy);
        }, [externalSetSelectedStep]);

    const setSelectedPhaseCb = useCallback(
        (phase: PhaseDto | null) => {
            const nextPhaseId = phase?.id ?? null;
            activePhaseIdRef.current = nextPhaseId;
            // Drop the cycle selection only when moving to a phase other than the one the
            // cycle belongs to. Same-phase re-selects (reload, step <-> summary) preserve it.
            if (selectedSubmissionIdRef.current !== null && nextPhaseId !== cyclePhaseIdRef.current) {
                selectedSubmissionIdRef.current = null;
                dispatch({ type: "SET_SUBMISSION_ID", payload: null });
            }
            dispatch({ type: "SELECT_PHASE", payload: phase });
            externalSetSelectedPhase(phase);
        }, [externalSetSelectedPhase]);

    const setSelectedStepFiles = useCallback(
        (type: CommentType, files: FileDto[] = EMPTY_FILES, stepId?: string) => {
            const targetStepId = stepId ?? getActiveStepId();
            if (!targetStepId) return;

            const byType = selectedStepFilesByStep.current[targetStepId] ?? {};
            selectedStepFilesByStep.current[targetStepId] = {
                ...byType,
                [type]: files ?? [],
            };
        },
        []
    );

    const updateSelectedStepFiles = useCallback(
        (filtered: FileDto[]) => {
            const step = selectedStepRef.current;
            if (!step) return;

            const updatedStep: StepDto = {
                ...step,
                requestComment: {
                    ...step.requestComment,
                    files: filtered,
                }
            };

            setSelectedStepCb(updatedStep);
        },
        [setSelectedStepCb]
    );

    const removeSelectedStepFile = useCallback((type: CommentType, fileId: string) => {
        const stepId = getActiveStepId();
        if (!stepId) return;

        const byType = selectedStepFilesByStep.current[stepId] ?? {};
        const curr = byType[type] ?? EMPTY_FILES;
        const filtered = curr.filter(f => f.id !== fileId && f.name !== fileId);
        if (filtered.length === curr.length) return;

        selectedStepFilesByStep.current[stepId] = { ...byType, [type]: filtered };
        if (type === CommentType.Request && tasklistMode === "templates") {
            updateSelectedStepFiles(filtered);
        }
    }, [tasklistMode, updateSelectedStepFiles]);


    const getSelectedStepFiles = useCallback(
        (type: CommentType): FileDto[] | null => {
            const stepId = getActiveStepId();
            if (!stepId) return null;
            const files = selectedStepFilesByStep.current[stepId]?.[type] ?? null;

            if (files == null && tasklistMode === "templates")
                return [];

            return files;
        },
        []
    );

    const hasStepFilesCache = useCallback(
        (type: CommentType, stepId?: string): boolean => {
            const targetStepId = stepId ?? getActiveStepId();
            if (!targetStepId) return false;
            return selectedStepFilesByStep.current[targetStepId]?.[type] !== undefined;
        },
        []
    );

    // split context: state changes frequently, actions are stable

    const selectionState = useMemo<ITaskListSelectionState>(
        () => ({
            selectedStep: state.selectedStep,
            selectedPhase: state.selectedPhase,
            tasklistMode: state.tasklistMode,
            selectedSubmissionId: state.selectedSubmissionId,
        }),
        [state.selectedStep, state.selectedPhase, state.tasklistMode, state.selectedSubmissionId]
    );

    const actions = useMemo<ITaskListActions>(
        () => ({
            setSelectedStep: setSelectedStepCb,
            setSelectedPhase: setSelectedPhaseCb,
            setTasklistMode,

            setSelectedSubmissionId,
            getSelectedSubmissionId,

            registerForClear,
            clearSelections,
            clearGridSelections,

            registerForRefreshSteps,
            refreshSteps,

            registerForRefreshFiles,
            refreshFiles,

            registerForRefreshComments,
            refreshComments,

            registerForRefreshSelectedStepFiles,
            refreshSelectedStepFiles,

            setSelectedStepFiles,
            removeSelectedStepFile,
            getSelectedStepFiles,
            hasStepFilesCache,
        }),
        [
            setSelectedStepCb,
            setSelectedPhaseCb,
            setTasklistMode,
            setSelectedSubmissionId,
            getSelectedSubmissionId,
            registerForClear,
            clearSelections,
            clearGridSelections,
            registerForRefreshSteps,
            refreshSteps,
            registerForRefreshFiles,
            refreshFiles,
            registerForRefreshComments,
            refreshComments,
            registerForRefreshSelectedStepFiles,
            refreshSelectedStepFiles,
            setSelectedStepFiles,
            removeSelectedStepFile,
            getSelectedStepFiles,
            hasStepFilesCache,
        ]
    );

    // legacy combined value for backward compatibility

    const legacyValue = useMemo<ITaskListContext>(
        () => ({
            ...selectionState,
            ...actions,
            getSelectedStep: () => selectionState.selectedStep,
            getSelectedPhase: () => selectionState.selectedPhase,
        }),
        [selectionState, actions]
    );

    return (
        <TaskListSelectionStateContext.Provider value={selectionState}>
            <TaskListActionsContext.Provider value={actions}>
                <TaskListContext.Provider value={legacyValue}>
                    {children}
                </TaskListContext.Provider>
            </TaskListActionsContext.Provider>
        </TaskListSelectionStateContext.Provider>
    );
}
